import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { LeadType, ObjectType, TaskCardType, TaskForType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useToast } from 'context/ToastContext';
import spinnerTitle from 'components/others/spinnerTitle';
import {
    isObjectBoundTaskFor,
    resolveTaskFor,
} from '../NewTask/helpers/initNewTaskStruct';
import TaskFor from './TaskFor';
import {
    bindingEntityLabel,
    buildBindingHistoryComment,
    type BindingHistoryLabels,
} from './helpers/taskBindingHelpers';

interface TaskBindingEditFormProps {
    task: TaskCardType;
    onCancel: () => void;
    onSaved: (task: TaskCardType) => void;
}

const leadFromTask = (task: TaskCardType): LeadType | null => {
    if (!task.leadInfo?.ID) return null;
    return {
        ID: task.leadInfo.ID,
        clientName: task.leadInfo.clientName || '',
        clientPhone: task.leadInfo.clientPhone || '',
        leadStatus: Number(task.leadInfo.leadStatus || 0),
    } as LeadType;
};

const objectFromTask = (task: TaskCardType): ObjectType | null => {
    if (!task.objectInfo?.ID) return null;
    return {
        ID: task.objectInfo.ID,
        objectName: task.objectInfo.objectName || '',
        clientName: task.objectInfo.clientName || '',
        onlySale: task.objectInfo.onlySale,
        onlyService: task.objectInfo.onlyService,
    } as ObjectType;
};

const entityName = (taskFor: TaskForType, lead: LeadType | null, object: ObjectType | null): string => {
    if (taskFor === 'lead') return String(lead?.clientName || '');
    if (isObjectBoundTaskFor(taskFor)) {
        return String(object?.objectName || object?.clientName || '');
    }
    return '';
};

const TaskBindingEditForm = ({ task, onCancel, onSaved }: TaskBindingEditFormProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const currentObject = objectFromTask(task);
    const currentLead = leadFromTask(task);
    const currentTaskFor = resolveTaskFor(
        task.leadId ? Number(task.leadId) : null,
        task.objectId ? Number(task.objectId) : null,
        currentObject,
    );

    const [taskFor, setTaskFor] = useState<TaskForType>(currentTaskFor);
    const [lead, setLead] = useState<LeadType | null>(currentTaskFor === 'lead' ? currentLead : null);
    const [object, setObject] = useState<ObjectType | null>(
        isObjectBoundTaskFor(currentTaskFor) ? currentObject : null,
    );
    const [saving, setSaving] = useState(false);

    const nextLeadId = taskFor === 'lead' ? Number(lead?.ID || 0) : 0;
    const nextObjectId = isObjectBoundTaskFor(taskFor) ? Number(object?.ID || 0) : 0;
    const selectionReady =
        taskFor === 'personal' ||
        (taskFor === 'lead' && nextLeadId > 0) ||
        (isObjectBoundTaskFor(taskFor) && nextObjectId > 0);
    const changed =
        nextLeadId !== Number(task.leadId || 0) || nextObjectId !== Number(task.objectId || 0);
    const canSave = selectionReady && changed;

    const labels = (): BindingHistoryLabels => ({
        personal: t('fields.personal'),
        lead: t('fields.lead'),
        object: t('fields.object'),
        sale: t('fields.sale'),
        service: t('fields.service'),
    });

    const handleSave = async () => {
        if (taskFor === 'lead' && !lead?.ID) {
            showToast(t('errEmptyLead'), 'danger');
            return;
        }
        if (isObjectBoundTaskFor(taskFor) && !object?.ID) {
            const errKey =
                taskFor === 'sale'
                    ? 'errEmptySale'
                    : taskFor === 'service'
                      ? 'errEmptyService'
                      : 'errEmptyObject';
            showToast(t(errKey), 'danger');
            return;
        }
        if (!changed) return;

        const historyLabels = labels();
        const fromLabel = bindingEntityLabel(
            currentTaskFor,
            entityName(currentTaskFor, currentLead, currentObject),
            historyLabels,
        );
        const toLabel = bindingEntityLabel(taskFor, entityName(taskFor, lead, object), historyLabels);

        setSaving(true);
        const result = await apiTask.updateTaskBinding(task.ID, {
            leadId: nextLeadId || null,
            objectId: nextObjectId || null,
            historyComment: buildBindingHistoryComment(fromLabel, toLabel),
        });
        setSaving(false);

        if (!result?.stat || !result.task) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        showToast(t('messBindingUpdated'), 'success');
        onSaved(result.task);
    };

    return (
        <div>
            <TaskFor
                taskFor={taskFor}
                lead={lead}
                object={object}
                disabled={saving}
                onTaskForChange={setTaskFor}
                onLeadChange={setLead}
                onObjectChange={setObject}
            />

            <div className="text-end">
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mb-1 min70px"
                    style={{ height: 24 }}
                    disabled={saving}
                    onClick={onCancel}
                >
                    {t('common:buttons.cancel')}
                </Button>
                <Button
                    type="button"
                    variant="success"
                    size="sm"
                    className="mb-1 ms-2 min70px"
                    style={{ height: 24 }}
                    disabled={saving || !canSave}
                    onClick={() => void handleSave()}
                >
                    {spinnerTitle(t('common:buttons.change'), saving, t('common:buttons.saving'))}
                </Button>
            </div>
        </div>
    );
};

export default TaskBindingEditForm;
